import React from 'react';
import classNames from 'classnames';
import styles from './index.module.scss';
import type { FeedbackProblemCategory, FeedbackProblemItem, FeedbackProblemPickerProps } from '../index';
import { findProblemByValue } from '../index';
import { releaseModalBodyScrollLock, syncModalBodyScrollLock } from '@utils/modalScrollLock';
import { BASE_IMG_URL } from '@images/imageUrls';

type TransitionState = 'hidden' | 'entering' | 'entered' | 'exiting';

interface H5FeedbackProblemPickerState {
    selectedValue: string;
    selectedProblem: FeedbackProblemItem | null;
    transitionState: TransitionState;
}

class H5FeedbackProblemPicker extends React.Component<FeedbackProblemPickerProps, H5FeedbackProblemPickerState> {
    state: H5FeedbackProblemPickerState = {
        selectedValue: '',
        selectedProblem: null,
        transitionState: 'hidden',
    };

    transitionTimer: ReturnType<typeof setTimeout> | null = null;

    /** 弹框显隐变化时：锁定/解锁页面滚动，并重置选中值 */
    componentDidUpdate(prevProps: FeedbackProblemPickerProps) {
        // 弹框打开时禁止页面滚动，仅允许弹框内列表滚动
        syncModalBodyScrollLock(this.props.visible, prevProps.visible);
        if (this.props.visible && !prevProps.visible) {
            if (this.transitionTimer) {
                clearTimeout(this.transitionTimer);
                this.transitionTimer = null;
            }
            const selectedValue = String(this.props.value || '');
            this.setState({
                selectedValue,
                selectedProblem: findProblemByValue(this.props.categories, selectedValue),
                transitionState: 'entering',
            });
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.setState({ transitionState: 'entered' });
                });
            });
            document.addEventListener('keydown', this.handleKeyDown);
        }

        if (!this.props.visible && prevProps.visible) {
            this.setState({ transitionState: 'exiting' });
            this.transitionTimer = setTimeout(() => {
                this.setState({ transitionState: 'hidden' });
                this.transitionTimer = null;
            }, 200);
            document.removeEventListener('keydown', this.handleKeyDown);
        }
    }

    /** 组件卸载时释放滚动锁 */
    componentWillUnmount() {
        if (this.transitionTimer) {
            clearTimeout(this.transitionTimer);
        }
        document.removeEventListener('keydown', this.handleKeyDown);
        releaseModalBodyScrollLock(this.props.visible);
    }

    handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            this.props.onClose();
        }
    };

    /** 确定：回传当前选中的问题类型 */
    handleConfirm = () => {
        const { onConfirm } = this.props;
        const { selectedProblem } = this.state;
        if (!selectedProblem) return;
        onConfirm(selectedProblem);
    };

    /** 重置：清空当前选中项，不关闭弹框 */
    handleReset = () => {
        this.setState({ selectedValue: '', selectedProblem: null });
    };

    /** 渲染底部抽屉选择器 */
    render() {
        const { categories, onClose } = this.props;
        const { selectedValue, selectedProblem, transitionState } = this.state;
        const isVisible = transitionState === 'entered';

        if (transitionState === 'hidden') return null;

        return (
            <div
                className={classNames(styles.mask, isVisible && styles.maskVisible)}
                onClick={onClose}
            >
                <div
                    className={classNames(styles.sheet, isVisible && styles.sheetVisible)}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className={styles.header}>
                        <div className={styles.title}>问题类型</div>
                        <img
                            src={`${BASE_IMG_URL}/feedback/icon6.png`}
                            alt="close"
                            className={styles.closeBtn}
                            onClick={onClose}
                        />
                    </div>

                    {/* data-body-scroll-lock-scrollable：弹框内可滚动区域，背景页禁止滚动 */}
                    <div className={styles.problemBody} data-body-scroll-lock-scrollable>
                        {categories.map((category: FeedbackProblemCategory) => (
                            <div key={category.id} className={styles.section}>
                                {category.name ? (
                                    <div className={styles.sectionTitle}>{category.name}</div>
                                ) : null}
                                <div className={styles.tagList}>
                                    {category.problems.map((problem) => {
                                        const checked = selectedValue === problem.value;
                                        return (
                                            <span
                                                key={problem.value}
                                                className={classNames(styles.tag, checked && styles.tagActive)}
                                                onClick={() => this.setState({
                                                    selectedValue: problem.value,
                                                    selectedProblem: problem,
                                                })}
                                            >
                                                {problem.name}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                        {!categories.length && (
                            <div className={styles.emptyText}>暂无问题类型</div>
                        )}
                    </div>

                    <div className={styles.footer}>
                        <button type="button" className={styles.cancelBtn} onClick={this.handleReset}>重置</button>
                        <button
                            type="button"
                            className={styles.confirmBtn}
                            onClick={this.handleConfirm}
                            disabled={!selectedProblem}
                        >
                            确定
                        </button>
                        
                    </div>
                </div>
            </div>
        );
    }
}

export default H5FeedbackProblemPicker;
